import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGetTenantQuery } from '../services/meApi';
import { useHistory } from 'react-router-dom';
import { getFirstPath } from '../utils';
import { SubscriptionType } from '../interfaces/tenant';

interface UseSubscriptionReturn {
  shouldShowModal: boolean;
  closeModal: () => void;
  /**
   * Returns `true` if tenant is verified.
   */
  isVerified: boolean;
  /**
   * Returns `true` if subscription is valid.
   */
  isSubscribed: boolean;
  tenantId?: number;
  subscriptionType?: SubscriptionType;
  subscriptionStartDate?: string;
  subscriptionEndDate?: string;
  isLoading: boolean;
  creationProcess: boolean;
}

export const useSubscription = (): UseSubscriptionReturn => {
  const history = useHistory();
  const [shouldShowModal, setShouldShowModal] = useState(false);

  const { data: tenant, isLoading, isFetching } = useGetTenantQuery();
  const isVerified = tenant?.verified ?? false;
  const isSubscribed = useMemo(
    () => tenant?.hasValidSubscription ?? false,
    [tenant?.hasValidSubscription],
  );

  const closeModal = useCallback(() => {
    setShouldShowModal(false);
    localStorage.setItem('shownPurchaseCheckOnce', 'true');
  }, []);

  useEffect(() => {
    if (isLoading || isFetching || !tenant) return;
    const shownPurchaseCheckOnce =
      localStorage.getItem('shownPurchaseCheckOnce') === 'true';
    const notInSubscriptionPage =
      getFirstPath(history.location?.pathname) !== '/annual-subscription';
    if (!shownPurchaseCheckOnce) {
      if (!isVerified && notInSubscriptionPage) {
        setShouldShowModal(true);
      }
    }
  }, [history.location?.pathname, isFetching, isLoading, isVerified, tenant]);

  return {
    shouldShowModal,
    closeModal,
    isVerified,
    isSubscribed,
    tenantId: tenant?.id,
    isLoading,
    subscriptionType: tenant?.subscriptionType,
    subscriptionStartDate: tenant?.subscriptionStartDate,
    subscriptionEndDate: tenant?.subscriptionEndDate,
    creationProcess: tenant?.creationProcess ?? false,
  };
};
